// src/encounter/services/use-delete-bundle.ts
// Archive a saved A/P bundle (PROD-1839; confirm + archive semantics PROD-2697).
//
// THERE IS NO DELETE ROUTE. Flutter has exactly ONE bundle write endpoint and create, update and
// delete all go through it (encounter_data_source_impl.dart:1309-1326):
//
//   POST /api/Patients/Bundle
//   { id, isDeleted, bundleName, providerId, bundleDetails }
//
//     · id = null           → CREATE
//     · id = <bundle id>    → UPDATE (upsert on the same row)
//     · isDeleted = true    → ARCHIVE. Since Scrips.Patient #1005 (PROD-2697) the server keeps the
//                             bundle's content and only flags the row. Only the bundle's CREATOR may
//                             archive it; a missing or not-owned bundle answers
//                             404 `{ "error": "The bundle was not found." }`.
//
// The archive path (RemoveBundleDataEvent, patient_encounter_bloc.dart:308-317) sends `bundleName`,
// `providerId` and `id` with `isDelete: true` and NO `bundleDetails` — the params object simply
// leaves it null, so the field serialises as null. We mirror that exactly rather than echoing the
// bundle back: sending the details on an archive would be inventing payload the source never sends.
//
// `useBundles` already filters `isDeleted` rows out of the list, so an archived bundle disappears on
// the next read even if the server keeps returning it.
//
// The hook keeps its historical `useDeleteBundle` name so the page and its many test mocks do not
// churn. The user-facing action is "Archive", and the confirm lives in ap-search-rail.tsx.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, post } from '@/shared/api/api-client';
import { showServerError } from '@/shared/lib/toast-helpers';
import type { ApBundle } from './use-bundles';

export interface DeleteBundleVars {
  bundle: ApBundle;
  /** The list's owner — the same `user.id` the fetch used, so the right cache entry is refreshed. */
  providerId: string;
}

/**
 * Archive a bundle, then refetch the provider's list.
 *
 * Flutter removes the row from its in-memory list optimistically and fires the call without
 * awaiting it (new_ap_bloc.dart:578-587). We invalidate instead: the list is a server query here,
 * and an optimistic removal that silently diverges from a failed call is the worse failure mode on
 * a surface the clinician will re-open.
 *
 * FAILURE: every error toasts through the app's standard `showServerError`. A 404 means the list is
 * stale (the bundle is already gone, or is not this provider's), so the list is refetched as well —
 * otherwise the row the clinician just tried to archive would stay there and fail again.
 */
export function useDeleteBundle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ bundle, providerId }: DeleteBundleVars) => {
      await post('/api/Patients/Bundle', {
        id: bundle.id ?? null,
        isDeleted: true,
        bundleName: bundle.bundleName ?? null,
        providerId,
        bundleDetails: null,
      });
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['ap-bundles', vars.providerId] });
    },
    onError: (err, vars) => {
      showServerError('Archiving the bundle', err);
      if (err instanceof ApiError && err.status === 404) {
        void qc.invalidateQueries({ queryKey: ['ap-bundles', vars.providerId] });
      }
    },
  });
}
